import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { apiUrl } from '../const/const';

export interface BuzzerPress {
  playerName: string;
  order: number;
}

/**
 * `sequential` — everybody may buzz, presses are ranked in order.
 * `lockout`    — the first press claims the buzzer and every later press is
 *                ignored until the host resets.
 */
export type BuzzerMode = 'sequential' | 'lockout';

export const normalizeBuzzerMode = (value?: string | null): BuzzerMode =>
  value === 'lockout' ? 'lockout' : 'sequential';

interface CreateGameResponse {
  success: boolean;
  code?: string;
  hostKey?: string;
  error?: string;
}

interface JoinGameResponse {
  success: boolean;
  playerName?: string;
  reconnected?: boolean;
  error?: string;
}

interface PressButtonResponse {
  success: boolean;
  alreadyPressed?: boolean;
  locked?: boolean;
  press?: BuzzerPress;
  error?: string;
}

export interface PressButtonResult {
  success: boolean;
  /** The press was refused by the lockout rules rather than by an actual failure. */
  locked: boolean;
}

interface ResetGameResponse {
  success: boolean;
  lockedOutPlayers?: string[];
  error?: string;
}

interface SetModeResponse {
  success: boolean;
  mode?: string;
  error?: string;
}

interface HubActionResponse {
  success: boolean;
  error?: string;
}

interface GameStateSnapshot {
  code: string;
  players: string[];
  presses: BuzzerPress[];
  lockedOutPlayers: string[];
  mode: string;
  hostConnected: boolean;
  isHost: boolean;
}

interface GameStateSnapshotResponse {
  success: boolean;
  state?: GameStateSnapshot;
  error?: string;
}

type SessionRole = 'host' | 'player' | null;

type SessionInfo = {
  role: SessionRole;
  code: string;
  hostKey?: string;
  playerName?: string;
};

const INVOKE_TIMEOUT_MS = 7000;

export const useBuzzerSignalR = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [players, setPlayers] = useState<string[]>([]);
  const [presses, setPresses] = useState<BuzzerPress[]>([]);
  const [isReset, setIsReset] = useState(false);
  const [hostConnected, setHostConnected] = useState(true);
  const [mode, setMode] = useState<BuzzerMode>('sequential');
  const [lockedOutPlayers, setLockedOutPlayers] = useState<string[]>([]);

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const sessionRef = useRef<SessionInfo>({ role: null, code: '' });

  const invokeWithTimeout = useCallback(async <T>(
    conn: signalR.HubConnection,
    method: string,
    args: any[] = [],
    timeoutMs = INVOKE_TIMEOUT_MS,
  ): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`${method} timeout`)), timeoutMs);
      });

      return await Promise.race([
        conn.invoke<T>(method, ...args),
        timeoutPromise,
      ]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }, []);

  const attachEventHandlers = useCallback((connection: signalR.HubConnection) => {
    connection.on('PlayerJoined', (playerName: string) => {
      setPlayers((prev) => (prev.includes(playerName) ? prev : [...prev, playerName]));
    });

    connection.on('PlayerRejoined', (playerName: string) => {
      setPlayers((prev) => (prev.includes(playerName) ? prev : [...prev, playerName]));
    });

    connection.on('PlayerLeft', (playerName: string) => {
      setPlayers((prev) => prev.filter((p) => p !== playerName));
    });

    connection.on('ButtonPressed', (press: BuzzerPress) => {
      setPresses((prev) => {
        if (prev.some((p) => p.playerName === press.playerName)) {
          return prev;
        }

        return [...prev, press].sort((a, b) => a.order - b.order);
      });
    });

    connection.on('GameReset', (payload?: { lockedOutPlayers?: string[] }) => {
      setPresses([]);
      setLockedOutPlayers(payload?.lockedOutPlayers ?? []);
      setIsReset(true);
      setTimeout(() => setIsReset(false), 100);
    });

    connection.on('ModeChanged', (nextMode: string) => {
      // The server starts a clean round whenever the mode changes.
      setMode(normalizeBuzzerMode(nextMode));
      setPresses([]);
      setLockedOutPlayers([]);
      setIsReset(true);
      setTimeout(() => setIsReset(false), 100);
    });

    connection.on('HostDisconnected', () => {
      setHostConnected(false);
    });

    connection.on('Error', (message: string) => {
      setError(message);
    });
  }, []);

  const syncGameState = useCallback(async (code: string, hostKey?: string) => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;

    try {
      const response = await invokeWithTimeout<GameStateSnapshotResponse>(
        conn,
        'GetGameState',
        [code, hostKey ?? null],
      );

      if (!response?.success || !response.state) {
        if (response?.error) setError(response.error);
        return;
      }

      setPlayers(response.state.players ?? []);
      setPresses((response.state.presses ?? []).slice().sort((a, b) => a.order - b.order));
      setLockedOutPlayers(response.state.lockedOutPlayers ?? []);
      setMode(normalizeBuzzerMode(response.state.mode));
      setHostConnected(Boolean(response.state.hostConnected));
    } catch (err: any) {
      setError(err?.message || 'Oyun vəziyyəti alına bilmədi.');
    }
  }, [invokeWithTimeout]);

  const recoverAfterReconnect = useCallback(async (connection: signalR.HubConnection) => {
    const session = sessionRef.current;
    if (!session.role || !session.code) return;

    try {
      if (session.role === 'host') {
        if (!session.hostKey) {
          setError('Host açarı tapılmadı. Yenidən otaq yaradın.');
          return;
        }

        const hostRejoin = await invokeWithTimeout<HubActionResponse>(
          connection,
          'RejoinAsHost',
          [session.code, session.hostKey],
        );

        if (!hostRejoin?.success) {
          setError(hostRejoin?.error || 'Moderator yenidən qoşula bilmədi.');
          return;
        }

        await syncGameState(session.code, session.hostKey);
        return;
      }

      if (!session.playerName) {
        setError('Oyunçu adı tapılmadı. Yenidən qoşulun.');
        return;
      }

      const playerRejoin = await invokeWithTimeout<JoinGameResponse>(
        connection,
        'JoinGame',
        [session.code, session.playerName],
      );

      if (!playerRejoin?.success) {
        setError(playerRejoin?.error || 'Oyuna yenidən qoşulmaq mümkün olmadı.');
        return;
      }

      await syncGameState(session.code);
    } catch (err: any) {
      setError(err?.message || 'Yenidən qoşulma xətası baş verdi.');
    }
  }, [invokeWithTimeout, syncGameState]);

  const startConnection = useCallback(async () => {
    const existing = connectionRef.current;
    if (existing) {
      if (
        existing.state === signalR.HubConnectionState.Connected
        || existing.state === signalR.HubConnectionState.Connecting
        || existing.state === signalR.HubConnectionState.Reconnecting
      ) {
        return existing;
      }

      try {
        await existing.stop();
      } catch {
        // best-effort cleanup before creating a fresh connection
      }

      connectionRef.current = null;
    }

    try {
      setError(null);
      const hubUrl = `${apiUrl}gameHub`;

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl)
        .withAutomaticReconnect()
        .build();

      attachEventHandlers(connection);

      connection.onreconnecting(() => {
        setIsConnected(false);
      });

      connection.onreconnected(async () => {
        setIsConnected(true);
        await recoverAfterReconnect(connection);
      });

      connection.onclose(() => {
        setIsConnected(false);
      });

      await connection.start();
      connectionRef.current = connection;
      setIsConnected(true);
      return connection;
    } catch (err: any) {
      console.error('SignalR Connection Error:', err);
      setError('Bağlantı xətası baş verdi.');
      return null;
    }
  }, [attachEventHandlers, recoverAfterReconnect]);

  const createGame = useCallback(async () => {
    try {
      const conn = await startConnection();
      if (!conn) return null;

      const response = await invokeWithTimeout<CreateGameResponse>(conn, 'CreateGame');
      if (!response?.success || !response.code || !response.hostKey) {
        setError(response?.error || 'Oyun yaradıla bilmədi.');
        return null;
      }

      sessionRef.current = {
        role: 'host',
        code: response.code,
        hostKey: response.hostKey,
      };

      setPlayers([]);
      setPresses([]);
      setLockedOutPlayers([]);
      setMode('sequential');
      setHostConnected(true);

      return {
        code: response.code,
        hostKey: response.hostKey,
      };
    } catch (err: any) {
      setError(err?.message || 'Oyun yaradıla bilmədi.');
      return null;
    }
  }, [invokeWithTimeout, startConnection]);

  /** Returns the name the server actually registered, or null when the join failed. */
  const joinGame = useCallback(async (code: string, playerName: string): Promise<string | null> => {
    try {
      const conn = await startConnection();
      if (!conn) return null;

      const response = await invokeWithTimeout<JoinGameResponse>(conn, 'JoinGame', [code, playerName]);
      if (!response?.success) {
        setError(response?.error || 'Oyuna qoşulmaq mümkün olmadı.');
        return null;
      }

      const resolvedName = response.playerName || playerName;
      sessionRef.current = {
        role: 'player',
        code,
        playerName: resolvedName,
      };

      await syncGameState(code);
      return resolvedName;
    } catch (err: any) {
      setError(err?.message || 'Oyuna qoşulmaq mümkün olmadı.');
      return null;
    }
  }, [invokeWithTimeout, startConnection, syncGameState]);

  const pressButton = useCallback(async (code: string): Promise<PressButtonResult> => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      return { success: false, locked: false };
    }

    try {
      const response = await invokeWithTimeout<PressButtonResponse>(conn, 'PressButton', [code]);
      if (!response?.success) {
        // A lockout is the rules working as intended — the UI shows it, no toast.
        if (!response?.locked) {
          setError(response?.error || 'Düymə sıxışı qeydə alınmadı.');
        }
        return { success: false, locked: Boolean(response?.locked) };
      }

      return { success: true, locked: false };
    } catch (err) {
      console.error('Error pressing button', err);
      setError('Düymə xətası baş verdi.');
      return { success: false, locked: false };
    }
  }, [invokeWithTimeout]);

  const invokeReset = useCallback(async (method: 'ResetGame' | 'ResetRound', code: string, hostKey?: string) => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      return false;
    }

    try {
      const response = await invokeWithTimeout<ResetGameResponse>(conn, method, [code, hostKey ?? null]);
      if (!response?.success) {
        setError(response?.error || 'Sıfırlama mümkün olmadı.');
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error resetting game', err);
      setError('Sıfırlama xətası baş verdi.');
      return false;
    }
  }, [invokeWithTimeout]);

  /** Full reset: clears the round and releases every locked-out player. */
  const resetGame = useCallback(
    (code: string, hostKey?: string) => invokeReset('ResetGame', code, hostKey),
    [invokeReset],
  );

  /** Partial reset: clears the round but keeps this round's buzzers locked out. */
  const resetRound = useCallback(
    (code: string, hostKey?: string) => invokeReset('ResetRound', code, hostKey),
    [invokeReset],
  );

  const setBuzzerMode = useCallback(async (code: string, hostKey: string, nextMode: BuzzerMode) => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      return false;
    }

    try {
      const response = await invokeWithTimeout<SetModeResponse>(conn, 'SetBuzzerMode', [code, hostKey, nextMode]);
      if (!response?.success) {
        setError(response?.error || 'Rejimi dəyişmək mümkün olmadı.');
        return false;
      }

      setMode(normalizeBuzzerMode(response.mode ?? nextMode));
      return true;
    } catch (err) {
      console.error('Error changing buzzer mode', err);
      setError('Rejim dəyişikliyi xətası baş verdi.');
      return false;
    }
  }, [invokeWithTimeout]);

  const rejoinAsHost = useCallback(async (code: string, hostKey: string) => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      return false;
    }

    try {
      const response = await invokeWithTimeout<HubActionResponse>(conn, 'RejoinAsHost', [code, hostKey]);
      if (!response?.success) {
        setError(response?.error || 'Moderator kimi qoşulmaq mümkün olmadı.');
        return false;
      }

      sessionRef.current = { role: 'host', code, hostKey };
      await syncGameState(code, hostKey);
      return true;
    } catch (err: any) {
      setError(err?.message || 'Moderator kimi qoşulma xətası.');
      return false;
    }
  }, [invokeWithTimeout, syncGameState]);

  const stopConnection = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.stop();
      connectionRef.current = null;
    }

    sessionRef.current = { role: null, code: '' };
    setIsConnected(false);
    setError(null);
    setPlayers([]);
    setPresses([]);
    setLockedOutPlayers([]);
    setMode('sequential');
    setHostConnected(true);
    setIsReset(false);
  }, []);

  useEffect(() => {
    return () => {
      stopConnection();
    };
  }, [stopConnection]);

  return {
    isConnected,
    error,
    players,
    presses,
    isReset,
    hostConnected,
    mode,
    lockedOutPlayers,
    createGame,
    joinGame,
    pressButton,
    resetGame,
    resetRound,
    setBuzzerMode,
    rejoinAsHost,
    syncGameState,
    stopConnection,
  };
};
